<template>
  <Page>
    <template #header>
      <h1 class="title is-3 has-text-centered">Suivi du poids</h1>
    </template>
    <template #body>
      <div class="container">
        <!-- Sélection de personne -->
        <div class="card mb-4">
          <div class="card-content">
            <div class="buttons has-addons is-centered">
              <button 
                class="button" 
                :class="person === 1 ? 'is-info' : 'is-light'"
                @click="choosePerson(1)"
              >
                Nicolas
              </button>
              <button 
                class="button" 
                :class="person === 2 ? 'is-danger' : 'is-light'"
                @click="choosePerson(2)"
              >
                Elodie
              </button>
            </div>
          </div>
        </div>

        <!-- Statistiques -->
        <div v-if="weights.length > 0" class="columns is-multiline mb-4">
          <div class="column is-4">
            <div class="card has-background-info-light">
              <div class="card-content">
                <p class="heading">Poids actuel</p>
                <p class="title is-3">{{ currentWeight }} kg</p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="card has-background-success-light">
              <div class="card-content">
                <p class="heading">Variation totale</p>
                <p class="title is-3" :class="weightVariation >= 0 ? 'has-text-success' : 'has-text-danger'">
                  {{ weightVariation >= 0 ? '+' : '' }}{{ weightVariation.toFixed(1) }} kg
                </p>
              </div>
            </div>
          </div>
          <div class="column is-4">
            <div class="card has-background-warning-light">
              <div class="card-content">
                <p class="heading">Nombre de mesures</p>
                <p class="title is-3">{{ weights.length }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Graphique -->
        <div v-if="weights.length > 0" class="card mb-4">
          <div class="card-header">
            <p class="card-header-title">
              <span class="icon is-small mr-2">
                <i class="fas fa-chart-line"></i>
              </span>
              Évolution du poids
            </p>
          </div>
          <div class="card-content">
            <div class="chart-container">
              <Line :data="chartData" :options="chartOptions" />
            </div>
          </div>
        </div>

        <!-- Formulaire d'ajout -->
        <div class="card mb-4">
          <div class="card-header">
            <p class="card-header-title">
              <span class="icon is-small mr-2">
                <i class="fas fa-plus-circle"></i>
              </span>
              Ajouter une mesure
            </p>
          </div>
          <div class="card-content">
            <div class="columns">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Poids (kg)</label>
                  <div class="control has-icons-left">
                    <input 
                      type="number" 
                      v-model.number="weight" 
                      step="0.1"
                      min="0"
                      max="300"
                      placeholder="Ex: 75.5" 
                      class="input is-medium"
                      @keyup.enter="addWeight"
                    >
                    <span class="icon is-small is-left">
                      <i class="fas fa-weight"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Date</label>
                  <div class="control has-icons-left">
                    <input 
                      type="date" 
                      v-model="selectedDate" 
                      class="input is-medium"
                      :max="maxDate"
                    >
                    <span class="icon is-small is-left">
                      <i class="fas fa-calendar"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="field">
              <div class="control">
                <button 
                  class="button is-primary is-medium is-fullwidth" 
                  @click="addWeight"
                  :disabled="!weight || weight <= 0"
                  :class="{'is-loading': loading}"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Ajouter le poids</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Liste des poids -->
        <div class="card">
          <div class="card-header">
            <p class="card-header-title">
              <span class="icon is-small mr-2">
                <i class="fas fa-list"></i>
              </span>
              Historique
              <span v-if="loading" class="icon is-small ml-2">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
            </p>
          </div>
          <div class="card-content">
            <div v-if="loading && weights.length === 0" class="has-text-centered py-6">
              <span class="icon is-large">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <p class="mt-2">Chargement...</p>
            </div>
            <div v-else-if="weights.length === 0" class="has-text-centered py-6">
              <span class="icon is-large has-text-grey">
                <i class="fas fa-inbox"></i>
              </span>
              <p class="mt-2 has-text-grey">Aucune mesure enregistrée</p>
            </div>
            <div v-else class="table-container">
              <table class="table is-fullwidth is-striped is-hoverable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th class="has-text-centered">Poids (kg)</th>
                    <th class="has-text-centered">Variation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(weight, index) in sortedWeights" :key="weight.id">
                    <td>
                      <span class="icon is-small mr-2">
                        <i class="fas fa-calendar-day"></i>
                      </span>
                      {{ formatDate(weight.date) }}
                    </td>
                    <td class="has-text-centered has-text-weight-bold">
                      {{ weight.content }} kg
                    </td>
                    <td class="has-text-centered">
                      <span 
                        v-if="index < sortedWeights.length - 1"
                        :class="getVariationClass(weight.content, sortedWeights[index + 1].content)"
                      >
                        {{ getVariation(weight.content, sortedWeights[index + 1].content) }}
                      </span>
                      <span v-else class="has-text-grey">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Page>
</template>

<script>
import axios from "axios";
import {base} from "../api";
import Page from "../Page.vue";
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: 'Weight',
  components: {
    Page,
    Line
  },
  data() {
    return {
      person: 2,
      weight: null,
      weights: [],
      loading: false,
      selectedDate: new Date().toISOString().split('T')[0],
      maxDate: new Date().toISOString().split('T')[0]
    }
  },
  computed: {
    sortedWeights() {
      return [...this.weights].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    currentWeight() {
      if (this.weights.length === 0) return 0;
      return this.sortedWeights[0].content;
    },
    weightVariation() {
      if (this.weights.length < 2) return 0;
      const sorted = this.sortedWeights;
      return sorted[0].content - sorted[sorted.length - 1].content;
    },
    chartData() {
      const sorted = [...this.weights].sort((a, b) => new Date(a.date) - new Date(b.date));
      return {
        labels: sorted.map(w => this.formatDateShort(w.date)),
        datasets: [
          {
            label: 'Poids (kg)',
            data: sorted.map(w => w.content),
            borderColor: this.person === 1 ? 'rgb(50, 115, 220)' : 'rgb(241, 70, 104)',
            backgroundColor: this.person === 1 ? 'rgba(50, 115, 220, 0.1)' : 'rgba(241, 70, 104, 0.1)',
            tension: 0.4,
            fill: true,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: this.person === 1 ? 'rgb(50, 115, 220)' : 'rgb(241, 70, 104)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 14
            },
            bodyFont: {
              size: 13
            },
            callbacks: {
              label: function(context) {
                return `Poids: ${context.parsed.y} kg`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: false,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              callback: function(value) {
                return value + ' kg';
              }
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      };
    }
  },
  async mounted() {
    await this.loadWeights();
  },
  methods: {
    async choosePerson(id) {
      this.person = id;
      await this.loadWeights();
    },
    async loadWeights() {
      this.loading = true;
      try {
        const response = await axios.get(base + '/weight/' + this.person);
        this.weights = response.data;
      } catch (error) {
        console.error('Erreur lors du chargement des poids:', error);
        this.weights = [];
      } finally {
        this.loading = false;
      }
    },
    async addWeight() {
      if (!this.weight || this.weight <= 0) {
        return;
      }
      
      this.loading = true;
      try {
        const body = {
          content: this.weight,
          date: this.selectedDate
        };
        
        const response = await axios.post(base + '/weight/' + this.person, body);
        this.weights.push(response.data);
        this.weight = null;
        this.selectedDate = new Date().toISOString().split('T')[0];
        
        // Notification de succès
        this.showNotification('Poids ajouté avec succès !', 'success');
      } catch (error) {
        console.error('Erreur lors de l\'ajout du poids:', error);
        this.showNotification('Une erreur est survenue', 'danger');
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatDateShort(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    },
    getVariation(current, previous) {
      const diff = current - previous;
      return diff >= 0 ? `+${diff.toFixed(1)} kg` : `${diff.toFixed(1)} kg`;
    },
    getVariationClass(current, previous) {
      const diff = current - previous;
      return diff > 0 ? 'has-text-danger' : diff < 0 ? 'has-text-success' : 'has-text-grey';
    },
    showNotification(message, type) {
      // Créer une notification simple
      const notification = document.createElement('div');
      notification.className = `notification is-${type} is-light`;
      notification.style.position = 'fixed';
      notification.style.top = '20px';
      notification.style.right = '20px';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.textContent = message;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 3000);
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.card-header-title {
  font-weight: 600;
  color: #495057;
}

.chart-container {
  position: relative;
  height: 300px;
  width: 100%;
}

.table th {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table td {
  vertical-align: middle;
}

.table.is-hoverable tbody tr:hover {
  background-color: #f8f9fa;
}

.buttons.has-addons .button {
  border-radius: 0;
}

.buttons.has-addons .button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.buttons.has-addons .button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.button {
  transition: all 0.2s ease;
}

.button:hover {
  transform: translateY(-1px);
}

.button:active {
  transform: translateY(0);
}

.input {
  border-radius: 4px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.table-container {
  border-radius: 4px;
  overflow: hidden;
}

/* Responsive design */
@media (max-width: 768px) {
  .columns.is-multiline {
    margin: 0;
  }
  
  .column {
    padding: 0.5rem;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .table {
    font-size: 0.875rem;
  }
  
  .card-content {
    padding: 1rem;
  }
  
  .chart-container {
    height: 250px;
  }
}

/* Animations */
.card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom colors */
.has-background-info-light {
  background-color: #e1f5fe !important;
  color: #0288d1;
}

.has-background-success-light {
  background-color: #e8f5e8 !important;
  color: #388e3c;
}

.has-background-warning-light {
  background-color: #fff3e0 !important;
  color: #f57c00;
}

.heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.5rem;
}
</style>
